/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business API型定義。API/Web 共有 contract package の型を再エクスポートし、UI 固有のパイプライン表示定数だけを保持する。
 * @graph-connects @cortex/domain-admin-api [references] cortex-admin API contract 型
 */

import type {
  AuthUser,
  BotHostMetrics,
  BotServiceStatus,
  BotStatusLatestResponse,
  BotStatusRecord,
  ClaudeBotUsage,
  ClaudeUsageLatestResponse,
  ClaudeUsageRange,
  CurrentUserResponse,
  LogoutResponse,
  PipelineConfig,
  PipelineConfigListResponse,
  PipelineStatus,
  PipelineType,
  TriggerPipelineResponse,
  FeatureOperators,
  PipelineFeatureId,
  PipelinePermissions,
  SavePipelinePermissionsRequest,
  WorkspacePermission,
  WorkspacePermissionList,
  WorkspacePermissionRole,
  WorkspacePermissionAdmins,
  SaveWorkspacePermissionRequest,
  UpsertWorkspacePermissionInput,
  RevokeWorkspacePermissionResponse,
  TargetDbOperators,
  WorkspaceUser,
  DataViewerJob,
  DataViewerJobListResponse,
  QueryResult,
  DataViewerJobDetail,
  UpdateDataViewerJobRequest,
  CreateDataViewerJobRequest,
  DataViewerJobIdResponse,
  DatabaseListResponse,
  FilterableColumn,
  FilterConditionInput,
  RawFilterConditionInput,
  DataViewerTable,
  DataViewerTableListResponse,
  DataViewerTableColumn,
  DescribeDataViewerTableResponse,
  PreviewQueryTablesRequest,
  PreviewTableColumnsRequest,
  PreviewJobQueryRequest,
  InfraGraphOverviewResponse,
  InfraGraphStackDetailResponse,
  InfraGraphStackSummary,
  InfraGraphStackEdge,
  InfraGraphResourceNode,
  InfraGraphResourceEdge,
  OwnershipTransferRecord,
  OwnershipTransferListResponse,
  OwnershipTransferListFilter,
  OwnershipTransferAction,
  OwnershipTransferReviewStatus,
  OwnershipTransferResourceType,
  MarkOwnershipTransferKeptRequest,
  MarkOwnershipTransferReassignedRequest,
  MarkOwnershipTransferDeletedRequest,
  GenerateThemesRequest,
  GenerateThemesResponse,
  GeneratedTheme,
  GenerateTitleRequest,
  GenerateTitleResponse,
  GeneratePagePromptsRequest,
  GeneratePagePromptsResponse,
  GeneratedPagePrompt,
  GenerateImageRequest,
  GenerateImageResponse,
  RecordContentDownloadRequest,
  RecordContentDownloadResponse,
  GenerateCaptionRequest,
  GenerateCaptionResponse,
  TiktokAuthUrlResponse,
  TiktokCallbackRequest,
  TiktokCallbackResponse,
  TiktokCreateDraftRequest,
  TiktokCreateDraftResponse,
  TiktokGetStatusRequest,
  TiktokGetStatusResponse,
} from '@cortex/domain-admin-api';
import type { PortalPermission, PortalRole } from '@cortex/core-types';

export type { PortalPermission, PortalRole };
export type {
  AuthUser,
  BotHostMetrics,
  BotServiceStatus,
  BotStatusLatestResponse,
  BotStatusRecord,
  ClaudeBotUsage,
  ClaudeUsageLatestResponse,
  ClaudeUsageRange,
  CurrentUserResponse,
  LogoutResponse,
  PipelineConfig,
  PipelineConfigListResponse,
  PipelineStatus,
  PipelineType,
  TriggerPipelineResponse,
  FeatureOperators,
  PipelineFeatureId,
  PipelinePermissions,
  SavePipelinePermissionsRequest,
  WorkspacePermission,
  WorkspacePermissionList,
  WorkspacePermissionRole,
  WorkspacePermissionAdmins,
  SaveWorkspacePermissionRequest,
  UpsertWorkspacePermissionInput,
  RevokeWorkspacePermissionResponse,
  TargetDbOperators,
  WorkspaceUser,
  DataViewerJob,
  DataViewerJobListResponse,
  QueryResult,
  DataViewerJobDetail,
  UpdateDataViewerJobRequest,
  CreateDataViewerJobRequest,
  DataViewerJobIdResponse,
  DatabaseListResponse,
  FilterableColumn,
  FilterConditionInput,
  RawFilterConditionInput,
  DataViewerTable,
  DataViewerTableListResponse,
  DataViewerTableColumn,
  DescribeDataViewerTableResponse,
  PreviewQueryTablesRequest,
  PreviewTableColumnsRequest,
  PreviewJobQueryRequest,
  InfraGraphOverviewResponse,
  InfraGraphStackDetailResponse,
  InfraGraphStackSummary,
  InfraGraphStackEdge,
  InfraGraphResourceNode,
  InfraGraphResourceEdge,
  OwnershipTransferRecord,
  OwnershipTransferListResponse,
  OwnershipTransferListFilter,
  OwnershipTransferAction,
  OwnershipTransferReviewStatus,
  OwnershipTransferResourceType,
  MarkOwnershipTransferKeptRequest,
  MarkOwnershipTransferReassignedRequest,
  MarkOwnershipTransferDeletedRequest,
  GenerateThemesRequest,
  GenerateThemesResponse,
  GeneratedTheme,
  GenerateTitleRequest,
  GenerateTitleResponse,
  GeneratePagePromptsRequest,
  GeneratePagePromptsResponse,
  GeneratedPagePrompt,
  GenerateImageRequest,
  GenerateImageResponse,
  RecordContentDownloadRequest,
  RecordContentDownloadResponse,
  GenerateCaptionRequest,
  GenerateCaptionResponse,
  TiktokAuthUrlResponse,
  TiktokCallbackRequest,
  TiktokCallbackResponse,
  TiktokCreateDraftRequest,
  TiktokCreateDraftResponse,
  TiktokGetStatusRequest,
  TiktokGetStatusResponse,
};

/**
 * パイプライン種別の値リスト。`PipelineType` 型・URL Zod schema・UI の種別セレクトが
 * 参照する単一の真実源（SoT）。新しい種別は共有 contract 側の PipelineType と合わせて追加する。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business パイプライン種別の値リスト（UI 用）。型・URL検証・UI ドロップダウンが共有する。
 * @graph-connects @cortex/domain-admin-api [references] PipelineType
 */
export const PIPELINE_TYPE_VALUES = [
  'database',
  'mongodb',
  'jobcan',
  'spreadsheet_sync',
  'drive',
] as const satisfies readonly PipelineType[];

/**
 * パイプライン種別の表示ラベル。`Record<PipelineType, string>` にすることで、
 * `PIPELINE_TYPE_VALUES` に種別を追加した際のラベル追加漏れを TS が検出する。
 * バッジ（PipelineTypeBadge）とフィルタの種別セレクト（PipelineFilterBar）が共有する。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business パイプライン種別の表示ラベル定数。バッジ・フィルタセレクトが共有する。
 * @graph-connects @cortex/domain-admin-api [references] PipelineType
 */
export const PIPELINE_TYPE_LABELS: Record<PipelineType, string> = {
  database: 'Database',
  mongodb: 'MongoDB',
  jobcan: 'Jobcan',
  spreadsheet_sync: 'Sheet Sync',
  drive: 'Drive',
};
